import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { getCurrentUser, signOut, fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import '@aws-amplify/ui-react/styles.css';
import Layout from './components/Layout';
import LoginSelection from './components/LoginSelection';
import RunbookList from './components/RunbookList';
import RunbookRoute from './components/RunbookRoute';
import FeatureRoute from './components/FeatureRoute';
// MODULES - Commented out for future use
// import ModuleList from './components/ModuleList';
// import CreateModule from './components/CreateModule';
// import EditModule from './components/EditModule';
// import ViewModule from './components/ViewModule';
// import Runbook from './components/Runbook';
import EditRunbook from './components/EditRunbook';
import ProdProductPerformance from './components/ProdProductPerformance';
import ViewRunbook from './components/ViewRunbook';
// import ImportRunbook from './components/ImportRunbook';
// import AddModule from './components/AddModule';
import IncidentView from './components/IncidentView';
import LandingRedirect from './components/LandingRedirect';
import PlanApproval from './components/PlanApproval';
import SnAnalysis from './components/SnAnalysis';
import NoAccess from './components/NoAccess';
import useUserPermissions from './hooks/useUserPermissions';
// import HomePage from './components/HomePage';
// import Resolution from './components/Resolution';
// import TribeRunbookSelection from './components/TribeRunbookSelection';
// import Classification from './components/Classification';
// import AIRunbookGenerator from './components/AIRunbookGenerator';
// import DraftRunbookList from './components/DraftRunbookList';
// import ReviewDraftRunbook from './components/ReviewDraftRunbook';
// import ProtectedRoute from './components/ProtectedRoute';
// import TestToken from './components/TestToken';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

function AuthenticatedRoutes() {
  const {
    canAccessIncidentResolver,
    canAccessProdPerformance,
    canAccessSnAnalysis,
  } = useUserPermissions();

  return (
    <Routes>
      {/* === Home Page: Role Selection (commented out for next release) === */}
      {/* <Route path="/" element={<HomePage />} /> */}

      {/* === Active: Incident View (migrated to AppSync GraphQL) === */}
      <Route path="/" element={<LandingRedirect />} />
      <Route path="/incident_view" element={<FeatureRoute canAccess={canAccessIncidentResolver()}><IncidentView /></FeatureRoute>} />
      <Route path="/sn-analysis" element={<FeatureRoute canAccess={canAccessSnAnalysis()}><SnAnalysis /></FeatureRoute>} />
      <Route path="/devops/sessions/:sessionId/plan" element={<FeatureRoute canAccess={canAccessIncidentResolver()}><PlanApproval /></FeatureRoute>} />
      <Route path="/prod-performance" element={<FeatureRoute canAccess={canAccessProdPerformance()}><ProdProductPerformance /></FeatureRoute>} />
      {/* Tribe performance merged into Prod Performance tab */}
      <Route path="/tribe-performance" element={<Navigate to="/prod-performance" replace />} />

      {/* === Runbooks: Migrated to AppSync GraphQL === */}
      {/* Guarded: Product Owner / Engineering Lead cannot access, even via direct URL */}
      <Route path="/runbooks" element={<RunbookRoute><RunbookList /></RunbookRoute>} />
      <Route path="/runbook_list" element={<RunbookRoute><RunbookList /></RunbookRoute>} />
      {/* <Route path="/create-runbook" element={<Runbook />} /> */}
      {/* <Route path="/import-runbook" element={<ImportRunbook />} /> */}
      <Route path="/edit-runbook/:id" element={<RunbookRoute><EditRunbook /></RunbookRoute>} />
      <Route path="/view-runbook/:id" element={<RunbookRoute><ViewRunbook /></RunbookRoute>} />
      {/* <Route path="/classification" element={<Classification />} /> */}
      {/* <Route path="/draft-runbooks" element={<ProtectedRoute requireModify={true}><DraftRunbookList /></ProtectedRoute>} /> */}
      {/* <Route path="/draft-runbooks/review/:runbookId" element={<ProtectedRoute requireModify={true}><ReviewDraftRunbook /></ProtectedRoute>} /> */}
      {/* <Route path="/resolution" element={<Resolution />} /> */}
      {/* <Route path="/select-runbook" element={<TribeRunbookSelection />} /> */}

      {/* === Modules - Commented out for future use === */}
      {/* <Route path="/modules" element={<ModuleList />} /> */}
      {/* <Route path="/module_list" element={<ModuleList />} /> */}
      {/* <Route path="/create-module" element={<CreateModule />} /> */}
      {/* <Route path="/edit-module/:id" element={<EditModule />} /> */}
      {/* <Route path="/view-module/:id" element={<ViewModule />} /> */}
      {/* <Route path="/add-modules/:runbookId" element={<AddModule />} /> */}
      <Route path="/no-access" element={<NoAccess />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);

  useEffect(() => {
    // Listen for auth events (OAuth callback completion, sign in, sign out)
    const hubListener = Hub.listen('auth', async (data) => {
      const { payload } = data;
      console.log('Auth event:', payload.event);
      
      if (payload.event === 'signedIn' || payload.event === 'signInWithRedirect') {
        console.log('User signed in via OAuth');
        await checkUser();
      } else if (payload.event === 'signInWithRedirect_failure') {
        console.error('Sign in failed:', payload.data);
        setShowLogin(true);
        setLoading(false);
      }
    });

    // Initial check
    checkUser();

    // Cleanup listener on unmount
    return () => hubListener();
  }, []);

  const checkUser = async () => {
    try {
      console.log('Checking user authentication...');
      
      // Get current session (this will process OAuth callback if present)
      const session = await fetchAuthSession();
      console.log('Session retrieved:', session.tokens ? 'Has tokens' : 'No tokens');
      
      if (!session.tokens) {
        throw new Error('No tokens in session');
      }
      
      // Get the current user
      const currentUser = await getCurrentUser();
      console.log('User authenticated:', currentUser.username || currentUser.userId);
      
      setUser(currentUser);
      setShowLogin(false);
      setLoading(false);
      
      // Clean up OAuth callback URL
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('code')) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } catch (error) {
      console.log('No user signed in:', error.message);
      setShowLogin(true);
      setLoading(false);
    }
  };

  const handleAuthSuccess = () => {
    // Refresh user state after successful authentication
    checkUser();
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setUser(null);
      setShowLogin(true);
    } catch (error) {
      console.log('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <h3>ResolveGuru</h3>
          <p>Loading...</p>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!user && showLogin) {
    return <LoginSelection onAuthSuccess={handleAuthSuccess} />;
  }
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Layout signOut={handleSignOut} user={user}> 
        <AuthenticatedRoutes />
      </Layout>
    </Router>
  );
}

// function App({ signOut, user }) {
//   return (
//     <Router>
//       <Layout signOut={signOut} user={user}>
//         <Routes>
//           <Route path="/" element={<RunbookList />} />
//           <Route path="/runbooks" element={<RunbookList />} />
//           <Route path="/runbook_list" element={<RunbookList />} />
//           <Route path="/modules" element={<ModuleList />} />
//           <Route path="/module_list" element={<ModuleList />} />
//           <Route path="/create-module" element={<CreateModule />} />
//           <Route path="/edit-module/:id" element={<EditModule />} />
//           <Route path="/view-module/:id" element={<ViewModule />} />
//           <Route path="/create-runbook" element={<Runbook />} />
//           <Route path="/edit-runbook/:id" element={<EditRunbook />} />
//           <Route path="/view-runbook/:id" element={<ViewRunbook />} />
//           <Route path="/add-modules/:runbookId" element={<AddModule />} />
//           <Route path="/incident-view" element={<IncidentView />} />
//           <Route path="/incident_view" element={<IncidentView />} />
//           <Route path="/resolution" element={<Resolution />} />
//         </Routes>
//       </Layout>
//     </Router>
//   );
// }

export default App;